import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PublicGuard } from 'app/core/auth/guards/public.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { mailboxGuard } from 'app/core/auth/guards/mailbox.guard';
import { mobileRedirectGuard } from 'app/core/auth/guards/mobile-redirect.guard';
import { kioskRedirectGuard } from 'app/core/auth/guards/kiosk-redirect.guard';
import { kioskModeEnabledGuard } from 'app/core/auth/guards/kiosk-access.guard';
// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // TEST ROUTE - Remove after confirming routing works
    {
        path: 'test-resource-view',
        loadComponent: () =>
            import('app/modules/admin/resources/resource-view/test-route.component').then(c => c.TestRouteComponent),
    },

    // Public resource view route (for outside resources with access code) - NO AUTH REQUIRED
    // Must be placed FIRST before any guards or redirects
    // This route is accessible without authentication - outside resources use access code
    // Using standalone component directly - no LayoutComponent wrapper to avoid any auth checks
    // Explicitly using PublicGuard to ensure no authentication is required
    // IMPORTANT: This route must come BEFORE the empty path redirect to ensure it matches first
    {
        path: 'resource-view',
        canActivate: [PublicGuard],
        loadComponent: () => {
            console.log('🟢🟢🟢 Route matched: resource-view - Loading component...');
            console.log('🟢 Window location:', window.location.href);
            console.log('🟢 Window hash:', window.location.hash);
            return import('app/modules/admin/resources/resource-view/resource-view.component').then(c => {
                console.log('🟢 Component loaded:', c.ResourceViewComponent);
                return c.ResourceViewComponent;
            });
        },
    },
    {
        path: 'photo-sample-review',
        canActivate: [PublicGuard],
        loadComponent: () =>
            import(
                'app/modules/admin/resources/photo-sample-review/photo-sample-review.component'
            ).then((c) => c.PhotoSampleReviewComponent),
    },

    // Public PWA install landing page. Shareable URL we hand out
    // (text / email / QR code) that takes recipients straight to
    // the install flow with no sign-in form in front of it.
    //
    // No auth guard — anyone can land here. The component itself
    // routes desktop visitors to /sign-in and standalone (already-
    // installed) visitors to /m/orders, so the URL doubles as a
    // smart-open link for users who already installed.
    {
        path: 'install',
        loadComponent: () =>
            import('app/modules/install/install.component').then(
                (c) => c.InstallLandingComponent,
            ),
    },

    // Public legal pages for A2P 10DLC / Twilio compliance are served as
    // STATIC HTML from `public/` so carriers & TCR reviewer bots (which
    // fetch server-side and do NOT run JavaScript) can read them, and so
    // there is a single, canonical source of truth. They live at:
    //   /privacy.html     — Privacy Policy
    //   /sms-optin.html   — SMS Messaging Program & Opt-In (Messaging Terms)
    //   /terms.html       — Terms of Service
    //   /company.html     — Business home page
    // These are real files, so the web server serves them directly and they
    // never enter Angular's (hash) router.

    // Kiosk mode control panel. Visiting `<site>/#/kiosk-setup` shows a
    // clear status page (kiosk ON/OFF) with explicit buttons to turn it on
    // or off, then a "Continue to sign in" button. Nothing changes on load,
    // so the URL can't silently flip the device. No auth guard: it only
    // reads/writes a local flag; sign-in still gates access.
    {
        path: 'kiosk-setup',
        loadComponent: () =>
            import('app/modules/kiosk/setup/kiosk-setup.component').then(
                (c) => c.KioskSetupComponent
            ),
    },

    // Redirect empty path to orders list
    { path: '', pathMatch: 'full', redirectTo: 'orders/list' },

    // Redirect signed-in user to orders list
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'orders/list',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'printer-sign-in',
                loadChildren: () =>
                    import('app/modules/auth/printer-sign-in/printer-sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
            {
                path: 'spline-preview',
                loadComponent: () =>
                    import(
                        'app/modules/landing/spline-preview/spline-preview.component'
                    ).then((m) => m.SplinePreviewComponent),
            },
        ],
    },

    // Mobile PWA routes - uses mobile layout with bottom navigation
    {
        path: 'm',
        // A kiosk (portrait) display must never land on the phone surface —
        // divert it to /kiosk during matching (no-op for non-kiosk devices).
        canMatch: [kioskRedirectGuard],
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'mobile', // Use mobile layout
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('app/modules/mobile/mobile.routes'),
            },
        ],
    },

    // Kiosk surface — a PORTRAIT presentation of the existing app for the
    // 42" panel. NOT a separate app/domain/login: it uses the same
    // AuthGuard + initialData resolver as the main routes, so the panel
    // shares the normal sign-in, user session and data. Only the layout
    // differs — its own minimal 'kiosk' shell (no Fuse nav chrome) and
    // portrait-first pages, so landscape components never have to be fought
    // into portrait. Kiosk mode is an explicit persisted flag (KioskService),
    // and the surface uses its own tunable zoom instead of the desktop one.
    {
        path: 'kiosk',
        canActivate: [AuthGuard, kioskModeEnabledGuard],
        canActivateChild: [AuthGuard, kioskModeEnabledGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'kiosk',
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('app/modules/kiosk/kiosk.routes'),
            },
        ],
    },

    // Main application routes (previously admin routes)
    {
        path: '',
        // Divert mobile devices to the mobile (/m/*) routes during route
        // MATCHING — before LayoutComponent / the resolver / the desktop
        // page ever mount. This prevents the desktop UI from briefly
        // flashing before the mobile view loads.
        // Kiosk guard runs first so a portrait panel is locked to /kiosk;
        // mobile guard then handles phones. Both are no-ops on landscape
        // desktop (and desktop PWA).
        canMatch: [kioskRedirectGuard, mobileRedirectGuard],
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'orders',
                loadChildren: () =>
                    import('app/modules/admin/orders/orders.routes'),
            },
            {
                path: 'artwork',
                loadChildren: () =>
                    import('app/modules/admin/artwork/artwork.routes'),
            },
            {
                path: 'purchasing',
                loadChildren: () =>
                    import('app/modules/admin/purchasing/purchasing.routes'),
            },
            {
                path: 'receiving',
                loadChildren: () =>
                    import('app/modules/admin/receiving/receiving.routes'),
            },
            {
                path: 'shipping',
                loadChildren: () =>
                    import('app/modules/admin/shipping/shipping.routes'),
            },
            {
                path: 'image-gallery',
                loadChildren: () =>
                    import(
                        'app/modules/admin/file-manager/file-manager.routes'
                    ),
            },
            {
                path: 'user-list',
                loadChildren: () =>
                    import(
                        'app/modules/admin/users/user-list/user-list.routes'
                    ),
            },
            {
                path: 'mailbox',
                canMatch: [mailboxGuard],
                loadChildren: () =>
                    import('app/modules/admin/mailbox/mailbox.routes'),
            },
            {   
                path: 'chat', 
                loadChildren: () => 
                    import('app/modules/admin/chat/chat.routes')
            },
            {
                path: 'printer-list',
                loadComponent: () => 
                    import('app/modules/admin/users/printer-list/printer-list.component').then(c => c.PrinterListComponent),
            },
            {
                path: 'printer-view',
                loadComponent: () =>
                    import('app/modules/admin/printer-view/printer-view.component').then(c => c.PrinterViewComponent),
            },
            {
                path: 'warehouse-vision',
                loadComponent: () =>
                    import('app/modules/admin/warehouse-vision/warehouse-vision.component').then(c => c.WarehouseVisionComponent),
            },
            {
                path: 'resources',
                loadChildren: () =>
                    import('app/modules/admin/resources/resources.routes'),
            },
            {
                path: 'resource-list',
                loadComponent: () => 
                    import('app/modules/admin/users/resource-list/resource-list.component').then(c => c.ResourceListComponent),
            },
            {
                path: 'courier-list',
                loadComponent: () =>
                    import('app/modules/admin/users/courier-list/courier-list.component').then(c => c.CourierListComponent),
            },
            {
                path: 'vendor-list',
                loadComponent: () => 
                    import('app/modules/admin/users/vendor-list/vendor-list.component').then(c => c.VendorListComponent),
            },
            {
                path: 'analytics',
                loadChildren: () =>
                    import('app/modules/admin/analytics/analytics.routes'),
            },
            {
                path: 'production-plan',
                loadChildren: () =>
                    import('app/modules/admin/production-plan/production-plan.routes'),
            },
            {
                path: 'assignment-rules',
                loadChildren: () =>
                    import('app/modules/admin/assignment-rules/assignment-rules.routes'),
            },
            {
                path: 'dashboard',
                loadChildren: () =>
                    import('app/modules/admin/operations-dashboard/operations-dashboard.routes'),
            },
            {
                path: 'delivery',
                loadChildren: () =>
                    import('app/modules/admin/delivery/delivery.routes'),
            },
            {
                path: 'system-admin',
                loadChildren: () =>
                    import('app/modules/admin/system-admin/system-admin.routes'),
            },
            {
                path: 'crm',
                loadChildren: () =>
                    import('app/modules/admin/crm-tool/crm-tool.routes'),
            },
            {
                path: 'products',
                loadChildren: () =>
                    import('app/modules/admin/products/products.routes'),
            },
            {
                path: 'shopify',
                loadChildren: () =>
                    import('app/modules/admin/shopify/shopify.routes'),
            },
        ],
    },

    // Redirects and catch-all routes
    { path: '404-not-found', redirectTo: 'orders/list' },
    { path: '**', redirectTo: 'orders/list' }
];
// To Disable Mail Page if email_token is null or not present
/* let userData: { email_token?: string | null } | null = null;
try {
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
        userData = JSON.parse(userDataString);
    }
} catch (e) {
    console.error('Error parsing userData from localStorage:', e);
}

if (!userData || userData.email_token == null) {
    const adminRoutesConfig = appRoutes.find(
        (route) => route.resolve && route.resolve.initialData === initialDataResolver
    );

    if (adminRoutesConfig && adminRoutesConfig.children) {
        adminRoutesConfig.children = adminRoutesConfig.children.filter(
            (childRoute) => childRoute.path !== 'mailbox'
        );
        console.log("Mailbox route ignored as email_token is null or not present.");
    }
} */